/**
 * Content Repurposer — IdeaWorks
 * Transform long-form content into platform-optimized posts for 7 platforms.
 * Platforms: X/Twitter, Instagram, TikTok, Reels (Meta), YouTube, Facebook, LinkedIn
 */
import { useState } from 'react';
import axios from 'axios';
import {
  Repeat2, Zap, RefreshCw, Copy, Check, ChevronDown, ChevronUp,
  ExternalLink, Clock, Hash, Target, Sparkles,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PLATFORMS = [
  { id: 'linkedin', name: 'LinkedIn', icon: '💼', color: '#0A66C2', desc: 'Professional post, 800-1300 chars' },
  { id: 'twitter_x', name: 'Twitter / X', icon: '🐦', color: '#1DA1F2', desc: 'Thread or tweet, max 280 chars' },
  { id: 'instagram', name: 'Instagram', icon: '📸', color: '#E1306C', desc: 'Caption with hashtags' },
  { id: 'tiktok', name: 'TikTok', icon: '🎵', color: '#69C9D0', desc: 'Hook-driven video script' },
  { id: 'reels_meta', name: 'Reels (Meta)', icon: '🎬', color: '#8B5CF6', desc: '30s video script' },
  { id: 'youtube', name: 'YouTube', icon: '▶️', color: '#FF0000', desc: 'Title, description & tags' },
  { id: 'facebook', name: 'Facebook', icon: '👥', color: '#1877F2', desc: 'Community post, 150-400 words' },
];

const CONTENT_TYPES = ['blog', 'video_transcript', 'newsletter', 'podcast', 'thread'];

function PlatformResult({ platform, result, isExpanded, onToggle }) {
  const [copied, setCopied] = useState(false);

  const getMainContent = () => {
    const r = result;
    return r?.tweet || r?.post || r?.caption || r?.script || r?.description || r?.content || '';
  };

  const copyAll = () => {
    const parts = [
      result?.title && `TITLE: ${result.title}`,
      getMainContent(),
      result?.hashtags?.length && `HASHTAGS: ${result.hashtags.join(' ')}`,
      result?.cta && `CTA: ${result.cta}`,
    ].filter(Boolean).join('\n\n');
    navigator.clipboard.writeText(parts);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!result?.success) {
    return (
      <div className="rounded-2xl p-4" style={{ background: 'rgba(255,59,59,0.06)', border: '1px solid rgba(255,59,59,0.2)' }}>
        <div className="flex items-center gap-3">
          <span className="text-lg">{platform.icon}</span>
          <div>
            <div className="text-xs font-bold text-white">{platform.name}</div>
            <div className="text-[11px] text-red-400">{result?.error || 'Generation failed'}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl overflow-hidden transition-all" style={{ border: `1px solid ${platform.color}30`, background: 'var(--llmc-surface)' }}>
      {/* Platform header */}
      <button onClick={onToggle} className="w-full flex items-center justify-between p-4 text-left group">
        <div className="flex items-center gap-3">
          <span className="text-xl">{platform.icon}</span>
          <div>
            <div className="text-sm font-bold text-white">{platform.name}</div>
            {result?.hook && (
              <div className="text-[11px] mt-0.5 italic" style={{ color: 'rgba(255,255,255,0.5)' }}>
                "{result.hook.slice(0, 60)}{result.hook.length > 60 ? '...' : ''}"
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {result?.best_time && (
            <div className="flex items-center gap-1 text-[10px] px-2 py-1 rounded-full"
              style={{ background: `${platform.color}15`, color: platform.color }}>
              <Clock size={9} /> {result.best_time}
            </div>
          )}
          {isExpanded
            ? <ChevronUp size={14} style={{ color: 'var(--llmc-text-muted)' }} />
            : <ChevronDown size={14} style={{ color: 'var(--llmc-text-muted)' }} />}
        </div>
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 space-y-3">
          {/* Title if present */}
          {result?.title && (
            <div>
              <div className="text-[10px] font-bold uppercase mb-1" style={{ color: platform.color, opacity: 0.8 }}>Title</div>
              <div className="text-xs font-semibold text-white">{result.title}</div>
            </div>
          )}

          {/* Main content */}
          <div>
            <div className="text-[10px] font-bold uppercase mb-1" style={{ color: platform.color, opacity: 0.8 }}>Content</div>
            <div className="text-xs leading-relaxed whitespace-pre-wrap p-3 rounded-xl"
              style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.8)', maxHeight: 300, overflowY: 'auto' }}>
              {getMainContent()}
            </div>
          </div>

          {/* Hashtags */}
          {result?.hashtags?.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {result.hashtags.map((h, i) => (
                <span key={i} className="flex items-center gap-0.5 text-[10px] px-2 py-0.5 rounded-full"
                  style={{ background: `${platform.color}15`, color: platform.color }}>
                  {h}
                </span>
              ))}
            </div>
          )}

          {/* CTA */}
          {result?.cta && (
            <div className="flex items-center gap-2 text-[11px] px-3 py-2 rounded-xl"
              style={{ background: `${platform.color}10`, color: platform.color }}>
              <Target size={11} /> {result.cta}
            </div>
          )}

          {/* Performance tip */}
          {result?.performance_tip && (
            <div className="text-[11px] italic px-3 py-2 rounded-xl"
              style={{ background: 'rgba(255,255,255,0.03)', color: 'rgba(255,255,255,0.45)', borderLeft: `2px solid ${platform.color}60` }}>
              {result.performance_tip}
            </div>
          )}

          {/* Copy button */}
          <button onClick={copyAll}
            className="flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all hover:opacity-80"
            style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--llmc-text-muted)' }}>
            {copied ? <Check size={11} style={{ color: '#3dffa0' }} /> : <Copy size={11} />}
            {copied ? 'Copied!' : 'Copy All'}
          </button>
        </div>
      )}
    </div>
  );
}

export default function ContentRepurposer() {
  const [content, setContent] = useState('');
  const [title, setTitle] = useState('');
  const [contentType, setContentType] = useState('blog');
  const [targetUrl, setTargetUrl] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState(PLATFORMS.map(p => p.id));
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [error, setError] = useState('');

  const togglePlatform = (pid) => {
    setSelectedPlatforms(prev =>
      prev.includes(pid) ? prev.filter(p => p !== pid) : [...prev, pid]
    );
  };

  const selectAll = () => setSelectedPlatforms(PLATFORMS.map(p => p.id));
  const clearAll = () => setSelectedPlatforms([]);

  const repurpose = async () => {
    if (content.trim().length < 50) { setError('Please paste at least 50 characters of content.'); return; }
    if (selectedPlatforms.length === 0) { setError('Select at least one platform.'); return; }
    setError('');
    setLoading(true);
    setResults(null);
    try {
      const { data } = await axios.post(`${API}/api/repurpose/transform`, {
        content,
        title,
        content_type: contentType,
        platforms: selectedPlatforms,
        target_url: targetUrl,
        brand_id: 'default',
      });
      setResults(data.platforms);
      // Auto-expand all successful results
      const exp = {};
      selectedPlatforms.forEach(p => { exp[p] = true; });
      setExpanded(exp);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Repurpose failed. Please try again.');
    }
    setLoading(false);
  };

  const wordCount = content.trim().split(/\s+/).filter(Boolean).length;
  const successCount = results ? Object.values(results).filter(r => r?.success).length : 0;

  return (
    <div className="flex-1 flex overflow-hidden">
      {/* Left: Input */}
      <div className="w-[420px] flex-shrink-0 flex flex-col overflow-hidden" style={{ borderRight: '1px solid var(--llmc-border)' }}>
        {/* Header */}
        <div className="p-4 flex-shrink-0" style={{ borderBottom: '1px solid var(--llmc-border)' }}>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(61,255,160,0.2), rgba(124,94,250,0.1))' }}>
              <Repeat2 size={16} style={{ color: '#3dffa0' }} />
            </div>
            <div>
              <h1 className="text-base font-black text-white">Content Repurposer</h1>
              <p className="text-[11px]" style={{ color: 'var(--llmc-text-muted)' }}>1 piece of content → 7 platform-optimized posts</p>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Title */}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider block mb-1.5" style={{ color: 'var(--llmc-text-muted)' }}>Title (optional)</label>
            <input
              value={title}
              onChange={e => setTitle(e.target.value)}
              className="llmc-input w-full px-3 py-2 text-sm"
              placeholder="Article or video title..."
            />
          </div>

          {/* Content type */}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider block mb-1.5" style={{ color: 'var(--llmc-text-muted)' }}>Content Type</label>
            <div className="flex flex-wrap gap-1.5">
              {CONTENT_TYPES.map(t => (
                <button key={t} onClick={() => setContentType(t)}
                  className="px-3 py-1 rounded-xl text-xs font-semibold transition-all"
                  style={{
                    background: contentType === t ? 'rgba(61,255,160,0.15)' : 'rgba(255,255,255,0.05)',
                    border: `1px solid ${contentType === t ? 'rgba(61,255,160,0.4)' : 'rgba(255,255,255,0.08)'}`,
                    color: contentType === t ? '#3dffa0' : 'var(--llmc-text-muted)',
                  }}>
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* Content input */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--llmc-text-muted)' }}>Long-Form Content</label>
              <span className="text-[10px]" style={{ color: wordCount > 50 ? '#3dffa0' : 'var(--llmc-text-muted)' }}>{wordCount} words</span>
            </div>
            <textarea
              value={content}
              onChange={e => setContent(e.target.value)}
              data-testid="repurpose-content-input"
              className="llmc-input w-full px-3 py-2.5 text-sm resize-none"
              rows={10}
              placeholder="Paste your blog article, video transcript, newsletter, podcast notes, or any long-form content here...

AI will transform it into platform-native content for all selected platforms."
            />
          </div>

          {/* Target URL */}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider block mb-1.5" style={{ color: 'var(--llmc-text-muted)' }}>Link Back URL (optional)</label>
            <input
              value={targetUrl}
              onChange={e => setTargetUrl(e.target.value)}
              className="llmc-input w-full px-3 py-2 text-sm"
              placeholder="https://qlia.io/blogs/your-article"
            />
          </div>

          {/* Platform selector */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--llmc-text-muted)' }}>
                Target Platforms ({selectedPlatforms.length}/{PLATFORMS.length})
              </label>
              <div className="flex gap-2">
                <button onClick={selectAll} className="text-[10px]" style={{ color: '#a68eff' }}>All</button>
                <span style={{ color: 'var(--llmc-text-muted)' }}>·</span>
                <button onClick={clearAll} className="text-[10px]" style={{ color: 'var(--llmc-text-muted)' }}>None</button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {PLATFORMS.map(p => (
                <button
                  key={p.id}
                  onClick={() => togglePlatform(p.id)}
                  className="flex items-center gap-2 px-2.5 py-2 rounded-xl text-xs font-medium transition-all text-left"
                  style={{
                    background: selectedPlatforms.includes(p.id) ? `${p.color}15` : 'rgba(255,255,255,0.03)',
                    border: `1px solid ${selectedPlatforms.includes(p.id) ? `${p.color}50` : 'rgba(255,255,255,0.08)'}`,
                    color: selectedPlatforms.includes(p.id) ? p.color : 'rgba(255,255,255,0.4)',
                  }}>
                  <span>{p.icon}</span>
                  <span className="truncate">{p.name}</span>
                </button>
              ))}
            </div>
          </div>

          {error && <div className="text-xs text-red-400 rounded-xl p-2" style={{ background: 'rgba(255,59,59,0.08)' }}>{error}</div>}

          {/* Repurpose button */}
          <button
            onClick={repurpose}
            disabled={loading || content.trim().length < 50 || selectedPlatforms.length === 0}
            data-testid="repurpose-btn"
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-black transition-all hover:scale-[1.02] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
            style={{ background: 'linear-gradient(135deg, #3dffa0, #00d4b8)', color: '#0a0a12' }}>
            {loading
              ? <><RefreshCw size={14} className="animate-spin" /> Repurposing {selectedPlatforms.length} platforms...</>
              : <><Repeat2 size={14} /> Repurpose → {selectedPlatforms.length} Platforms</>
            }
          </button>
        </div>
      </div>

      {/* Right: Results */}
      <div className="flex-1 overflow-y-auto">
        {/* Results header */}
        {results && (
          <div className="sticky top-0 z-10 px-5 py-3 flex items-center justify-between"
            style={{ background: 'var(--llmc-bg)', borderBottom: '1px solid var(--llmc-border)' }}>
            <div className="flex items-center gap-3">
              <Sparkles size={15} style={{ color: '#3dffa0' }} />
              <span className="text-sm font-bold text-white">
                {successCount}/{selectedPlatforms.length} platforms generated
              </span>
            </div>
            <div className="flex gap-2">
              <button onClick={() => { const e = {}; selectedPlatforms.forEach(p => { e[p] = true; }); setExpanded(e); }}
                className="text-[11px] px-2 py-1 rounded-lg" style={{ color: '#a68eff', background: 'rgba(124,94,250,0.1)' }}>
                Expand All
              </button>
              <button onClick={() => setExpanded({})}
                className="text-[11px] px-2 py-1 rounded-lg" style={{ color: 'var(--llmc-text-muted)', background: 'rgba(255,255,255,0.05)' }}>
                Collapse
              </button>
            </div>
          </div>
        )}

        <div className="p-5 space-y-3">
          {loading && (
            <div className="flex flex-col items-center justify-center py-24 space-y-5">
              <div className="relative w-16 h-16">
                <div className="absolute inset-0 rounded-2xl animate-ping" style={{ background: 'rgba(61,255,160,0.15)' }} />
                <div className="relative w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(61,255,160,0.15)' }}>
                  <Repeat2 size={28} style={{ color: '#3dffa0' }} />
                </div>
              </div>
              <div className="text-center">
                <div className="text-sm font-bold text-white mb-1">Repurposing your content...</div>
                <div className="text-xs" style={{ color: 'var(--llmc-text-muted)' }}>
                  Generating {selectedPlatforms.length} platform-native versions in parallel
                </div>
              </div>
              <div className="flex gap-2 flex-wrap justify-center">
                {selectedPlatforms.map(pid => {
                  const p = PLATFORMS.find(pl => pl.id === pid);
                  return p ? (
                    <div key={pid} className="flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full animate-pulse"
                      style={{ background: `${p.color}15`, color: p.color }}>
                      {p.icon} {p.name}
                    </div>
                  ) : null;
                })}
              </div>
            </div>
          )}

          {!loading && !results && (
            <div className="flex flex-col items-center justify-center py-24 space-y-4">
              <div className="w-20 h-20 rounded-3xl flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, rgba(61,255,160,0.1), rgba(124,94,250,0.08))' }}>
                <Repeat2 size={36} style={{ color: 'rgba(61,255,160,0.5)' }} />
              </div>
              <div className="text-center max-w-sm">
                <div className="text-base font-black text-white mb-2">Transform Content Once, Post Everywhere</div>
                <div className="text-xs leading-relaxed" style={{ color: 'var(--llmc-text-muted)' }}>
                  Paste your long-form content on the left, select your platforms, and let AI generate
                  native, engagement-optimized posts for each channel simultaneously.
                </div>
              </div>
              <div className="flex flex-wrap gap-2 justify-center">
                {PLATFORMS.map(p => (
                  <div key={p.id} className="flex items-center gap-1.5 text-xs px-3 py-1 rounded-full"
                    style={{ background: `${p.color}10`, color: p.color, border: `1px solid ${p.color}30` }}>
                    <span>{p.icon}</span> {p.name}
                  </div>
                ))}
              </div>
            </div>
          )}

          {!loading && results && selectedPlatforms.map(pid => {
            const platform = PLATFORMS.find(p => p.id === pid);
            if (!platform) return null;
            return (
              <PlatformResult
                key={pid}
                platform={platform}
                result={results[pid]}
                isExpanded={!!expanded[pid]}
                onToggle={() => setExpanded(e => ({ ...e, [pid]: !e[pid] }))}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
